import { Link } from "react-router-dom";
import { Heart, Plus } from "lucide-react";
import { money, imgUrl } from "@/lib/api";
import { KiddeeSeal } from "@/components/KiddeeSeal";
import { Stars, TopRatedBadge, isTopRated } from "@/components/Stars";
import { useCart } from "@/context/CartContext";
import { useFavorites } from "@/context/FavoritesContext";
import { SHOP } from "@/constants/testIds";

export const ProductCard = ({ product, index = 0 }) => {
  const { add } = useCart();
  const { slugs, toggle } = useFavorites();
  const fav = slugs.includes(product.slug);
  return (
    <div data-testid={SHOP.productCard(product.slug)} className="group fade-up bg-white border border-[#F0EBE1] rounded-[1.5rem] overflow-hidden flex flex-col hover:shadow-lg transition-shadow duration-300" style={{ animationDelay: `${Math.min(index, 12) * 40}ms` }}>
      <div className="relative">
        <Link to={`/product/${product.slug}`} className="block aspect-square overflow-hidden bg-[#F0EBE1]">
          <img src={imgUrl(product.image)} alt={product.name} loading="lazy" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
        </Link>
        {product.kiddee_fav ? (
          <KiddeeSeal testId={SHOP.kiddeeSeal(product.slug)} className="absolute top-3 left-3 pointer-events-none" />
        ) : product.badge && (
          <span className="absolute top-3 left-3 text-[11px] font-semibold uppercase tracking-wider bg-white/90 px-2.5 py-1 rounded-full">
            {product.badge}
          </span>
        )}
        <button
          data-testid={SHOP.favButton(product.slug)}
          aria-label={fav ? "Remove from favorites" : "Save to favorites"}
          onClick={() => toggle(product)}
          className="absolute top-2 right-2 w-11 h-11 rounded-full bg-white/90 grid place-items-center hover:scale-110 transition-transform"
        >
          <Heart size={16} className={fav ? "fill-[#E11D48] text-[#E11D48]" : "text-stone-700"} />
        </button>
      </div>
      <div className="p-4 flex flex-col flex-1">
        <Link to={`/product/${product.slug}`} className="font-heading font-bold leading-snug hover:text-[#E06D44] transition-colors line-clamp-2">
          {product.name}
        </Link>
        {product.rating && (
          <div data-testid={SHOP.cardRating(product.slug)} className="mt-1.5 flex flex-wrap items-center gap-1.5 text-xs text-stone-500" title={`${product.rating.average} out of 5`}>
            <Stars value={product.rating.average} size={13} />
            <span>{product.rating.average} ({product.rating.count})</span>
            {isTopRated(product.rating) && <TopRatedBadge testId={SHOP.topRated(product.slug)} />}
          </div>
        )}
        <div className="mt-auto pt-3 flex flex-col gap-3">
          <span data-testid={SHOP.cardPrice(product.slug)} className="font-semibold text-lg">{money(product.price)}</span>
          <button
            data-testid={SHOP.addButton(product.slug)}
            onClick={() => add(product)}
            aria-label={`Add ${product.name} to cart`}
            className="min-h-[44px] w-full rounded-full bg-[#1C1917] text-white text-sm font-semibold flex items-center justify-center gap-1.5 hover:bg-[#E06D44] transition-colors"
          >
            <Plus size={16} /> Add to cart
          </button>
        </div>
      </div>
    </div>
  );
};
